/**
 * MSAL Configuration for Azure Entra External ID (CIAM)
 * 
 * This configuration supports Entra External ID (CIAM) authentication,
 * which uses different authority URLs than Azure AD B2C.
 */

import type { Configuration, RedirectRequest, SilentRequest } from '@azure/msal-browser'
import { LogLevel } from '@azure/msal-browser'

const localAuthBypassEnabled = import.meta.env.VITE_LOCAL_DEV_AUTH_BYPASS === 'true'

// CIAM tenant information from environment variables
const ciamTenantSubdomain = import.meta.env.VITE_CIAM_TENANT_SUBDOMAIN || 'beaconcustomers'
const ciamTenantId = import.meta.env.VITE_CIAM_TENANT_ID || ''
const ciamClientId = import.meta.env.VITE_CIAM_CLIENT_ID || ''

// Build CIAM authority URL
// Format: https://{tenantSubdomain}.ciamlogin.com/{tenantId}
const ciamAuthority = `https://${ciamTenantSubdomain}.ciamlogin.com/${ciamTenantId}`
const ciamAuthorityDomain = `${ciamTenantSubdomain}.ciamlogin.com`

export const ciamConfig = {
  tenantSubdomain: ciamTenantSubdomain,
  tenantId: ciamTenantId,
  clientId: ciamClientId,
  authority: ciamAuthority,
  authorityDomain: ciamAuthorityDomain,
}

/**
 * MSAL Configuration
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: ciamClientId,
    authority: ciamAuthority,
    knownAuthorities: [ciamAuthorityDomain],
    redirectUri: import.meta.env.VITE_CIAM_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: import.meta.env.VITE_CIAM_POST_LOGOUT_REDIRECT_URI || window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // More secure than localStorage
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return
        }
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message)
            break
          case LogLevel.Warning:
            console.warn('[MSAL]', message)
            break
          case LogLevel.Info:
            console.info('[MSAL]', message)
            break
          case LogLevel.Verbose:
            console.debug('[MSAL]', message)
            break
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Verbose : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
  },
}

/**
 * Scopes for API access
 * These should match the scopes exposed by your backend API
 */
const apiScopesString = import.meta.env.VITE_CIAM_API_SCOPES || ''
export const apiScopes = apiScopesString.split(' ').filter(Boolean)

/**
 * Login request configuration.
 * Empty scopes — MSAL.js adds OIDC scopes (openid, profile, email) automatically.
 * API access tokens are acquired separately via acquireTokenSilent after login.
 */
export const loginRequest: RedirectRequest = {
  scopes: [],
}

/**
 * Silent token request configuration
 */
export const silentRequest: SilentRequest = {
  scopes: apiScopes,
  forceRefresh: false,
}

/**
 * Helper to check if CIAM is properly configured
 */
export function isCiamConfigured(): boolean {
  return Boolean(ciamTenantSubdomain && ciamClientId && ciamTenantId)
}

/**
 * Local development auth bypass.
 * When enabled, frontend auth guards and token acquisition are skipped.
 */
export function isLocalAuthBypassEnabled(): boolean {
  return localAuthBypassEnabled
}

/**
 * Get API base URL from environment
 */
export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
}

/**
 * Get File Service URL from environment
 */
export function getFileServiceUrl(): string {
  return import.meta.env.VITE_FILE_SERVICE_URL || 'http://localhost:8081'
}

/**
 * Get Analysis Service URL from environment
 */
export function getAnalysisServiceUrl(): string {
  return import.meta.env.VITE_ANALYSIS_SERVICE_URL || 'http://localhost:8080'
}
